import Button from 'components/_UI/Button';
import Ilustration from 'components/_UI/Ilustration';
import Text from 'components/_UI/Text';
import React, { useEffect, useState } from 'react'
import { PlusSquare, Trash2, Users } from 'lucide-react';
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column'
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import PermissionsModal from './PermissionsModal';
import collaboratorsApi from 'api/requests/collaborators';
import teamsApi from 'api/requests/teams';
import { useEvent } from 'context/events/Event';
import { FullyCentered } from 'styled/alignment/Center';
import Loader from 'components/_UI/Loader';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';
import EventAccessTable from './EventAccessTable';
import useTeamPermissionCatalog from 'modules/dashboard/producer/teams/useTeamPermissionCatalog';
import DeletePermissionModal from './DeletePermissionModal';
import TeamGrantForEventModal from './TeamGrantForEventModal';
import ConfirmDialog from 'modules/restaurant/menu/components/ConfirmDialog';
import { BlockContent } from 'styled/UI/BlockContent';

export default function CollaboratorsPermissions() {
    const [modalVisible, setModalVisible] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [content, setContent] = useState(false);
    const [selected, setSelected] = useState(false);
    const [edit, setEdit] = useState(false);
    const [loading, setLoading] = useState(false);
    const [teamGrantModalVisible, setTeamGrantModalVisible] = useState(false);
    const [linkedTeams, setLinkedTeams] = useState([]);
    const [accessTeams, setAccessTeams] = useState([]);
    const [loadingTeams, setLoadingTeams] = useState(false);
    const [confirmRemoveTeamGrant, setConfirmRemoveTeamGrant] = useState({ open: false, teamId: null, grantId: null, name: '' });
    const [removingTeamGrant, setRemovingTeamGrant] = useState(false);

    const { event } = useEvent();
    const { toast, toasts } = useToast();
    const { labelOf } = useTeamPermissionCatalog();

    useEffect(() => {
        getCollaboratorsPermissions();
        getLinkedTeams();
    }, []);

    async function getCollaboratorsPermissions() {
        setLoading(true);

        try {
            const { data } = await collaboratorsApi.getList(event?.slug);
            setContent(data?.data);
        } catch (error) {

        } finally {
            setLoading(false);
        }
    }

    async function getLinkedTeams() {
        setLoadingTeams(true);

        try {
            const { data } = await teamsApi.get();
            const teams = (data?.data || []).map(team => ({
                ...team,
                eventGrant: (team.grants || []).find(grant => grant.scope_type === 'event' && grant.event?.id === event?.id),
                panelGrant: (team.grants || []).find(grant => grant.scope_type === 'panel'),
            }));

            // Só a vinculação ao evento se remove daqui; o acesso pelo painel
            // inteiro se gerencia na página de equipes.
            setLinkedTeams(teams.filter(team => team.eventGrant));
            setAccessTeams(teams.filter(team => team.eventGrant || team.panelGrant));
        } catch (error) {

        } finally {
            setLoadingTeams(false);
        }
    }

    function onTeamGrantSuccess() {
        setTeamGrantModalVisible(false);
        getLinkedTeams();
        toast.success('Equipe vinculada com sucesso!', { position: 'top-center' })
    }

    function onRemoveTeamGrantClick(team) {
        setConfirmRemoveTeamGrant({ open: true, teamId: team.id, grantId: team.eventGrant.id, name: team.name });
    }

    async function handleRemoveTeamGrant() {
        setRemovingTeamGrant(true);

        try {
            await teamsApi.removeGrant(confirmRemoveTeamGrant.teamId, confirmRemoveTeamGrant.grantId);
            getLinkedTeams();
            toast.success('Acesso da equipe removido com sucesso!', { position: 'top-center' })
        } catch (error) {

        } finally {
            setRemovingTeamGrant(false);
            setConfirmRemoveTeamGrant({ open: false, teamId: null, grantId: null, name: '' });
        }
    }

    function onSuccess() {
        setModalVisible(false);
        setSelected(false);
        getCollaboratorsPermissions();
        toast.success('Convite enviado com sucesso!', { position: 'top-center' })
    }

    const PANEL_PERMISSION_LABELS = {
        'event.edit': 'Editar evento',
        'event.sales.view': 'Ver pedidos e vendas',
        'participants.view': 'Ver participantes',
    }

    function renderPermissionList(data) {
        if (data === 'ticket') {
            return 'Validar ingressos'
        }

        if (data === 'product') {
            return 'Validar produtos'
        }

        if (data === 'both') {
            return 'Validar ingressos, Validar produtos'
        }
    }

    function renderPanelPermissionList(item) {
        return (item?.permissions || [])
            .map(permission => PANEL_PERMISSION_LABELS[permission])
            .filter(Boolean)
            .join(', ')
    }

    function onEditClick(item) {
        setEdit(item);
        setSelected(item);
        setModalVisible(true);
    }

    function onDeleteClick(item) {
        setSelected(item);
        setDeleteModalVisible(true);
    }

    function renderTeamPermissions(team) {
        return (team.permissions || []).map(labelOf).join(', ');
    }

    const collaboratorRows = (content || []).map(item => ({
        id: `collaborator-${item?.id}`,
        email: item?.email,
        permissions: [renderPermissionList(item?.permission_type), renderPanelPermissionList(item)].filter(Boolean).join(', '),
        origin: null,
        status: item?.status,
        onEdit: () => onEditClick(item),
        onDelete: () => onDeleteClick(item),
    }));

    // Quem entra pela equipe tem acesso ao evento sem ter sido convidado
    // nele; mostra todos os membros que ainda podem acessar, com a equipe de
    // origem. Recusado não tem acesso.
    const teamMemberRows = accessTeams.flatMap(team => (
        (team.members || [])
            .filter(member => member.status !== 'rejected')
            .map(member => ({
                id: `team-${team.id}-${member.id}`,
                email: member.email,
                permissions: renderTeamPermissions(team),
                origin: team.eventGrant ? team.name : `${team.name} (painel inteiro)`,
                status: member.status,
            }))
    ));

    const accessRows = [...collaboratorRows, ...teamMemberRows];

    function onClose() {
        setModalVisible(false);
        setEdit(false);
    }

    function onDeleteModal() {
        setSelected(false);
        setDeleteModalVisible(false);
        getCollaboratorsPermissions();
        toast.success('Permissão excluida com sucesso!', { position: 'top-center' })
    }


    return (
        <Container>
            <ToastContainer toasts={toasts} />
            <HeaderContent>
                <Column width='auto' paddingTop='12px'>
                    <Text name='subtitle' color={colors.nightDark}>
                        Permissões de colaboradores
                    </Text>
                    <Text>
                        Gerencie o acesso e permissões da sua equipe
                    </Text>
                </Column>

                <ButtonsRow>
                    <Button
                        variant='light'
                        category='squired'
                        onClick={() => setTeamGrantModalVisible(true)}
                    >
                        <Users color={colors.primary} size={16} />
                        <Text
                            color={colors.primary}
                            paddingBottom={0}
                            marginLeft={'6px'}
                        >
                            Vincular equipe
                        </Text>
                    </Button>

                    <Button
                        variant='featured'
                        category='squired'
                        onClick={() => setModalVisible(true)}
                    >
                        <PlusSquare color={colors.neutralLight} size={16} />
                        <Text
                            color={colors.neutralLight}
                            paddingBottom={0}
                            marginLeft={'6px'}
                        >
                            Convidar colaborador
                        </Text>
                    </Button>
                </ButtonsRow>
            </HeaderContent>

            <BlockContent>


                {
                    modalVisible && (
                        <PermissionsModal
                            visible={modalVisible}
                            edit={edit}
                            onClose={onClose}
                            onSuccess={onSuccess}
                        />
                    )
                }

                {
                    deleteModalVisible && (
                        <DeletePermissionModal
                            visible={deleteModalVisible}
                            data={selected}
                            onClose={() => setDeleteModalVisible(false)}
                            onSuccess={onDeleteModal}
                        />
                    )
                }

                {
                    teamGrantModalVisible && (
                        <TeamGrantForEventModal
                            visible={teamGrantModalVisible}
                            event={event}
                            onClose={() => setTeamGrantModalVisible(false)}
                            onSuccess={onTeamGrantSuccess}
                        />
                    )
                }

                <ConfirmDialog
                    isOpen={confirmRemoveTeamGrant.open}
                    title='Remover acesso da equipe'
                    message={`Tem certeza que deseja remover o acesso da equipe "${confirmRemoveTeamGrant.name}" a este evento?`}
                    confirmLabel='Remover'
                    danger
                    loading={removingTeamGrant}
                    onConfirm={handleRemoveTeamGrant}
                    onCancel={() => setConfirmRemoveTeamGrant({ open: false, teamId: null, grantId: null, name: '' })}
                />

                {
                    !loadingTeams && linkedTeams.length > 0 && (
                        <LinkedTeamsSection>
                            <Text name='highlight' color={colors.nightDark}>
                                Equipes com acesso a este evento
                            </Text>

                            {
                                linkedTeams.map(team => (
                                    <LinkedTeamRow key={team.id}>
                                        <Text paddingBottom={0}>{team.name}</Text>
                                        <ActionButton onClick={() => onRemoveTeamGrantClick(team)}>
                                            <Trash2 color={colors.danger} size={18} />
                                        </ActionButton>
                                    </LinkedTeamRow>
                                ))
                            }
                        </LinkedTeamsSection>
                    )
                }

                {
                    loading || loadingTeams ? (
                        <ContentLoader>
                            <Loader />
                        </ContentLoader>
                    ) : (
                        accessRows.length
                            ? (
                                <>
                                    <Text name='highlight' color={colors.nightDark}>
                                        Pessoas com acesso ao evento
                                    </Text>
                                    <Text marginBottom={'10px'}>
                                        Colaboradores convidados e membros das equipes vinculadas, com as permissões e a origem de cada acesso.
                                    </Text>

                                    <EventAccessTable rows={accessRows} />
                                </>
                            ) : (
                                <Column fullyCentralized height='auto'>
                                    <Picture>
                                        <Ilustration name='collaboration' />
                                    </Picture>

                                    <Text type='bold' color={colors.night} textAlign={'center'} name='normal' marginTop={'-35px'}>
                                        {
                                            accessTeams.length
                                                ? 'As equipes vinculadas ainda não têm membros com acesso a este evento.'
                                                : 'Ninguém além de você tem acesso a este evento.'
                                        }
                                    </Text>

                                    <Text type='bold' color={colors.night} textAlign={'center'} name='normal'>
                                        Que tal convidar alguém ou vincular uma equipe?
                                    </Text>

                                    <FullyCentered marginTop='10px'>
                                        <Column width='250px'>
                                            <Button
                                                variant='featured'
                                                category='squired'
                                                onClick={() => setModalVisible(true)}
                                            >
                                                <PlusSquare color={colors.neutralLight} size={16} />
                                                <Text
                                                    color={colors.neutralLight}
                                                    paddingBottom={0}
                                                    marginLeft={'6px'}
                                                >
                                                    Convidar colaborador
                                                </Text>
                                            </Button>
                                        </Column>
                                    </FullyCentered>
                                </Column>
                            )
                    )
                }
            </BlockContent>

        </Container>
    )
}


const Container = styled(Column)`
    width: 100%;
`;

const HeaderContent = styled(Column)`
    width: 100%;
    padding-bottom: 20px;

    & > button {
        margin-top: 10px;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;

        & > h2,
        & > button {
            width: auto;
            margin-top: 0;
        }

        
    }
`;

const ButtonsRow = styled(Column)`
    width: 100%;
    gap: 10px;

    @media screen and (min-width: ${breakpoints.md}) {
        width: auto;
        flex-direction: row;

        & > button {
            width: auto;
            white-space: nowrap;
        }
    }
`;

const Picture = styled.picture`
    width: auto;
    height: 300px;
`;

const LinkedTeamsSection = styled(Column)`
    width: 100%;
    margin-bottom: 24px;
`;

const LinkedTeamRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    margin-top: 8px;
    border-radius: 10px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
`;

const ActionButton = styled.div`
    width: auto;
    height: auto;
    transition: all ease-in-out .25s;
    cursor: pointer;

    ${({ disabled }) => disabled && `
        opacity: .4;
        pointer-events: none;
    `};
`;

const ContentLoader = styled(Column)`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;